'use strict';

/**
 * Moodboard-MediaImageSource
 * --------------------------
 * Adapts the plansheet Media library to the image-source interface that pict-section-moodboard's view
 * expects (options.ImageSource). The moodboard gallery is driven entirely by whatever this object
 * declares, so the customer's uploaded Media shows up as the board's gallery with search / filter /
 * sort, and dropped or pasted image files get stored as Media owned by the open board.
 *
 * The board owner is read lazily through getOwnerId() so one source instance serves every board the
 * user opens (the owner is the current IDMoodboard). Media owned by (OwnerType 'Moodboard',
 * IDOwner = that id) is the gallery.
 *
 * Interface used by the moodboard view:
 *   getFields()                       -> field descriptors (drives the gallery's filter/sort controls)
 *   getFilterOptions(key)             -> distinct values for a filterable field
 *   list({ Search, Filters, Sort })   -> Promise<item[]> ({ Id, Name, Url, Thumbnail, Metadata })
 *   upload(file, dataUrl, callback)   -> store the bytes as Media, callback(null, { Url, Id, Name })
 *   add(record)                       -> no-op: an external URL is not a Media blob, so it is left on
 *                                        the board but not added to the customer's Media gallery
 */

const _FIELDS =
[
	{ Key: 'Name', Label: 'Name', Type: 'string', Searchable: true, Sortable: true },
	{ Key: 'MediaType', Label: 'Type', Type: 'enum', Filterable: true, Sortable: true, Searchable: true },
	{ Key: 'AddedAt', Label: 'Added', Type: 'date', Sortable: true }
];

const _MEDIA_TYPES = ['Screenshot', 'Photo', 'Drawing', 'Recording', 'HowTo', 'Other'];

class MoodboardMediaImageSource
{
	constructor(pOptions)
	{
		let tmpOptions = pOptions || {};
		this._MediaAPI = tmpOptions.MediaAPI || null;
		// A function returning the current board id (IDMoodboard) the gallery is scoped to.
		this._getOwnerId = (typeof tmpOptions.getOwnerId === 'function') ? tmpOptions.getOwnerId : function () { return null; };
		this._OwnerType = tmpOptions.OwnerType || 'Moodboard';
		// Boards that hang off an entity report a falsy owner while none is open, so a falsy id normally
		// means "no bucket yet" and the gallery stays empty. A board that belongs to the PLAN SHEET rather
		// than to a record (the top-bar backdrop, WI #360) has no entity id at all, and cannot invent one:
		// IDCustomer must never travel client-side. Such a source opts in here, and IDOwner 0 under its own
		// OwnerType becomes a real, tenant-scoped bucket -- the server scopes every Media read and write to
		// the session's plan sheet, so 0 is unambiguous.
		this._AllowZeroOwner = (tmpOptions.AllowZeroOwner === true);
	}

	// Is there a bucket to read from / write to? Zero counts only for a source that opted in above.
	_hasOwner(pOwnerId)
	{
		if (this._AllowZeroOwner) { return (pOwnerId !== null && typeof pOwnerId !== 'undefined'); }
		return !!pOwnerId;
	}

	getFields()
	{
		return _FIELDS.map((pField) => Object.assign({}, pField));
	}

	getFilterOptions(pKey)
	{
		if (pKey === 'MediaType') { return _MEDIA_TYPES.slice(); }
		return [];
	}

	list(pQuery)
	{
		let tmpOwnerId = this._getOwnerId();
		if (!this._MediaAPI || !this._hasOwner(tmpOwnerId))
		{
			return Promise.resolve([]);
		}
		return this._MediaAPI.listForOwner(this._OwnerType, tmpOwnerId)
			.then((pRows) =>
			{
				let tmpItems = (Array.isArray(pRows) ? pRows : []).map((pRow) => this._toItem(pRow));
				return this._applyQuery(tmpItems, pQuery || {});
			})
			.catch(() => []);
	}

	upload(pFile, pDataUrl, fCallback)
	{
		let tmpOwnerId = this._getOwnerId();
		if (!this._MediaAPI || !this._hasOwner(tmpOwnerId))
		{
			// No place to store it; fall back to the data URL so the image still lands on the board.
			if (typeof fCallback === 'function') { fCallback(null, { Url: pDataUrl, Name: (pFile && pFile.name) || 'image' }); }
			return;
		}
		this._MediaAPI.upload(this._OwnerType, tmpOwnerId, pFile, {})
			.then((pRef) =>
			{
				let tmpID = pRef && pRef.IDMedia;
				if (typeof fCallback === 'function') { fCallback(null, { Url: this._MediaAPI.blobURL(tmpID), Id: tmpID, Name: (pFile && pFile.name) || 'image' }); }
			})
			.catch((pError) =>
			{
				// On failure, still place the image via its data URL so the user does not lose the drop.
				if (typeof fCallback === 'function') { fCallback(null, { Url: pDataUrl, Name: (pFile && pFile.name) || 'image' }); }
			});
	}

	// An external URL (pasted/dropped link) is not a Media blob; leave it on the board, out of the gallery.
	add(pRecord) { return null; }

	// Fetch one gallery item by its id, for a drawing card's "Update from source". Media is scoped to the
	// open board's owner, so find it within that owner's set (Promise<item|null>).
	getItem(pId)
	{
		let tmpOwnerId = this._getOwnerId();
		if (!this._MediaAPI || !this._hasOwner(tmpOwnerId) || pId == null) { return Promise.resolve(null); }
		return this._MediaAPI.listForOwner(this._OwnerType, tmpOwnerId)
			.then((pRows) =>
			{
				let tmpRow = (Array.isArray(pRows) ? pRows : []).find((pRow) => String(pRow.IDMedia) === String(pId));
				return tmpRow ? this._toItem(tmpRow) : null;
			})
			.catch(() => null);
	}

	_toItem(pRow)
	{
		let tmpURL = this._MediaAPI ? this._MediaAPI.blobURL(pRow.IDMedia) : '';
		return {
			Id: pRow.IDMedia,
			Name: pRow.Title || pRow.OriginalFileName || ('Media ' + pRow.IDMedia),
			Url: tmpURL,
			Thumbnail: tmpURL,
			// MimeType + OriginalFileName let a drawing card tell an SVG source from a raster one (so an SVG
			// drawing is inlined as vector into the board SVG rather than referenced as a raster <image>).
			Metadata: { MediaType: pRow.MediaType || 'Other', MimeType: pRow.MimeType || '', OriginalFileName: pRow.OriginalFileName || '', AddedAt: pRow.CreateDate || '', SizeBytes: pRow.FileSizeBytes || 0 }
		};
	}

	_fieldValue(pItem, pKey)
	{
		if (pKey === 'Name') { return pItem.Name; }
		return (pItem.Metadata && typeof pItem.Metadata[pKey] !== 'undefined') ? pItem.Metadata[pKey] : '';
	}

	_applyQuery(pItems, pQuery)
	{
		let tmpItems = pItems.slice();

		let tmpSearch = (pQuery.Search || '').trim().toLowerCase();
		if (tmpSearch)
		{
			tmpItems = tmpItems.filter((pItem) =>
			{
				let tmpHay = (pItem.Name + ' ' + this._fieldValue(pItem, 'MediaType')).toLowerCase();
				return tmpHay.indexOf(tmpSearch) >= 0;
			});
		}

		let tmpFilters = pQuery.Filters || {};
		Object.keys(tmpFilters).forEach((pKey) =>
		{
			let tmpValue = tmpFilters[pKey];
			if (tmpValue === '' || tmpValue == null) { return; }
			tmpItems = tmpItems.filter((pItem) => String(this._fieldValue(pItem, pKey)) === String(tmpValue));
		});

		if (pQuery.Sort && pQuery.Sort.Field)
		{
			let tmpField = pQuery.Sort.Field;
			let tmpDir = (pQuery.Sort.Direction === 'desc') ? -1 : 1;
			tmpItems.sort((pA, pB) =>
			{
				let tmpAV = this._fieldValue(pA, tmpField);
				let tmpBV = this._fieldValue(pB, tmpField);
				if (tmpAV < tmpBV) { return -1 * tmpDir; }
				if (tmpAV > tmpBV) { return 1 * tmpDir; }
				return 0;
			});
		}

		return tmpItems;
	}
}

module.exports = MoodboardMediaImageSource;
